import { AtomMapUiPreferences, MapStyle, MapTypeId } from '@atom/types/map';
import { safeParseStorage } from '@atom/utilities/localStorageUtilities';

const ATOM_MAP_STATE_KEY = 'atomMapState';
const LEGACY_MAP_STATE_KEY = 'gmapState';

export const DEFAULT_PREFERENCES: AtomMapUiPreferences = {
  mapStyle: 'default',
  mapTypeId: 'roadmap',
  traffic: false,
  poi: false,
};

const VALID_STYLES: MapStyle[] = ['default', 'silver', 'retro', 'dark'];
const VALID_TYPES: MapTypeId[] = ['roadmap', 'satellite', 'hybrid', 'terrain'];

// Validate an arbitrary parsed object down to the four known preferences,
// falling back field-by-field to defaults. Drops any transient v1 fields.
const coercePreferences = (raw: unknown): AtomMapUiPreferences => {
  const rawData = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    unknown
  >;

  return {
    mapStyle: VALID_STYLES.includes(rawData.mapStyle as MapStyle)
      ? (rawData.mapStyle as MapStyle)
      : DEFAULT_PREFERENCES.mapStyle,
    mapTypeId: VALID_TYPES.includes(rawData.mapTypeId as MapTypeId)
      ? (rawData.mapTypeId as MapTypeId)
      : DEFAULT_PREFERENCES.mapTypeId,
    traffic:
      typeof rawData.traffic === 'boolean'
        ? rawData.traffic
        : DEFAULT_PREFERENCES.traffic,
    poi:
      typeof rawData.poi === 'boolean' ? rawData.poi : DEFAULT_PREFERENCES.poi,
  };
};

export const readAtomMapState = (): AtomMapUiPreferences =>
  coercePreferences(
    safeParseStorage(ATOM_MAP_STATE_KEY) ?? DEFAULT_PREFERENCES,
  );

// Used to migrate old localStorage to new localStorage when the feature is released.
export const migrateLegacyMapState = (): AtomMapUiPreferences | null => {
  const legacy = safeParseStorage(LEGACY_MAP_STATE_KEY);
  return legacy ? coercePreferences(legacy) : null;
};

export const writeAtomMapState = (preferences: AtomMapUiPreferences): void => {
  localStorage.setItem(ATOM_MAP_STATE_KEY, JSON.stringify(preferences));
};

export const atomMapStateExists = (): boolean =>
  localStorage.getItem(ATOM_MAP_STATE_KEY) !== null;
